import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Form, Row, Col, Badge, Card, Alert } from 'react-bootstrap';
import {
  FaArrowRight,
  FaGripVertical,
  FaSearch,
  FaTimes,
  FaThList,
  FaTrash,
} from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  fetchAuthorizedTabsForEdit,
  fetchTabPreferences,
  saveTabPreferences,
} from '../../api/TabPreferenceApi';
import { isHomeNavTab, emitNavTabsUpdated } from '../../utils/navTabs';
import {
  buildBarSlotsFromTabs,
  flattenBarSlotsToTabs,
  removeTabFromBarSlots,
  addTabToBarSlots,
  getTabGroupKey,
  getTabGroupLabel,
} from '../../utils/tabBarSlots';
import './css/EnhancedListViewDialog.css';
import './css/ManageTabsModal.css';

function normalizeTab(tab) {
  return {
    id: String(tab.id),
    label: tab.label || tab.name || 'Tab',
    name: tab.name || '',
    order_no: tab.order_no ?? 0,
    url: tab.url,
    object_name: tab.object_name,
    config: tab.config,
    group_key: getTabGroupKey(tab),
    group_label: getTabGroupLabel(tab),
  };
}

function buildInitialLists(authorizedTabs, preferences) {
  const prefMap = new Map(
    (preferences || []).map((p) => [String(p.app_tab_id), p])
  );
  const homeTabs = authorizedTabs.filter(isHomeNavTab).map(normalizeTab);
  const rest = authorizedTabs.filter((t) => !isHomeNavTab(t));

  const sortedRest = [...rest].sort((a, b) => {
    const pa = prefMap.get(String(a.id));
    const pb = prefMap.get(String(b.id));
    const orderA =
      pa?.order_no != null && pa.order_no !== '' ? Number(pa.order_no) : Number(a.order_no) ?? 0;
    const orderB =
      pb?.order_no != null && pb.order_no !== '' ? Number(pb.order_no) : Number(b.order_no) ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    return String(a.label || a.name || '').localeCompare(String(b.label || b.name || ''));
  });

  const selected = [];
  const available = [];

  sortedRest.forEach((tab) => {
    const pref = prefMap.get(String(tab.id));
    const row = normalizeTab(tab);
    if (pref && pref.is_visible === false) {
      available.push(row);
    } else {
      selected.push(row);
    }
  });

  homeTabs.forEach((home) => {
    if (!selected.some((t) => t.id === home.id)) {
      selected.unshift(home);
    }
  });

  return { selected, available };
}

function buildPreferencesPayload(visibleTabs, availableTabs) {
  const preferences = [];
  let order = 1;
  visibleTabs.forEach((tab) => {
    preferences.push({
      app_tab_id: tab.id,
      is_visible: true,
      order_no: order++,
    });
  });
  availableTabs.forEach((tab) => {
    preferences.push({
      app_tab_id: tab.id,
      is_visible: false,
      order_no: order++,
    });
  });
  return preferences;
}

function reorderBarSlots(barSlots, sourceIndex, destIndex) {
  const list = [...barSlots];
  const [removed] = list.splice(sourceIndex, 1);
  list.splice(destIndex, 0, removed);
  return list;
}

function reorderGroupTabs(barSlots, groupKey, sourceIndex, destIndex) {
  return barSlots.map((slot) => {
    if (slot.type !== 'group' || slot.key !== groupKey) return slot;
    const tabs = [...(slot.tabs || [])];
    const [removed] = tabs.splice(sourceIndex, 1);
    tabs.splice(destIndex, 0, removed);
    return { ...slot, tabs };
  });
}

const ManageTabsModal = ({ show, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [barSlots, setBarSlots] = useState([]);
  const [availableTabs, setAvailableTabs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [authorized, preferences] = await Promise.all([
        fetchAuthorizedTabsForEdit(),
        fetchTabPreferences().catch(() => []),
      ]);
      const { selected, available } = buildInitialLists(authorized, preferences);
      setBarSlots(buildBarSlotsFromTabs(selected));
      setAvailableTabs(available);
    } catch (err) {
      console.error('ManageTabsModal load error:', err);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to load tabs'
      );
      setBarSlots([]);
      setAvailableTabs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      setSearchTerm('');
      loadData();
    }
  }, [show, loadData]);

  const getFilteredAvailable = () => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return availableTabs;
    return availableTabs.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.name.toLowerCase().includes(q) ||
        (t.group_label || '').toLowerCase().includes(q)
    );
  };

  const moveToSelected = (tab) => {
    setAvailableTabs((prev) => prev.filter((t) => t.id !== tab.id));
    setBarSlots((prev) => addTabToBarSlots(prev, tab));
  };

  const moveToAvailable = (tab) => {
    if (isHomeNavTab(tab)) return;
    setBarSlots((prev) => removeTabFromBarSlots(prev, tab.id));
    setAvailableTabs((prev) => [...prev.filter((t) => t.id !== tab.id), tab]);
  };

  const handleBarSlotDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    if (result.source.droppableId !== 'manage-tabs-bar-slots') return;
    setBarSlots((prev) =>
      reorderBarSlots(prev, result.source.index, result.destination.index)
    );
  };

  const handleGroupTabsDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const droppableId = result.source.droppableId;
    if (!droppableId.startsWith('manage-tabs-group-')) return;
    const groupKey = droppableId.replace('manage-tabs-group-', '');
    setBarSlots((prev) =>
      reorderGroupTabs(prev, groupKey, result.source.index, result.destination.index)
    );
  };

  const handleDragEnd = (result) => {
    if (result.source.droppableId === 'manage-tabs-bar-slots') {
      handleBarSlotDragEnd(result);
    } else {
      handleGroupTabsDragEnd(result);
    }
  };

  const handleSave = async () => {
    const visibleTabs = flattenBarSlotsToTabs(barSlots);
    if (visibleTabs.length === 0) {
      setError('Select at least one tab to show in navigation');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const preferences = buildPreferencesPayload(visibleTabs, availableTabs);
      const result = await saveTabPreferences(preferences);
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to save tab preferences');
      }
      emitNavTabsUpdated();
      onClose();
    } catch (err) {
      console.error('ManageTabsModal save error:', err);
      setError(
        err?.response?.data?.message || err?.message || 'Failed to save tab preferences'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const groupBarSlots = barSlots.filter((s) => s.type === 'group');
  const visibleTabCount = flattenBarSlotsToTabs(barSlots).length;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      className="manage-tabs-modal"
      dialogClassName="manage-tabs-modal-dialog enhanced-list-view-modal"
      container={typeof document !== 'undefined' ? document.body : undefined}
      backdrop={saving ? 'static' : true}
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          <FaThList className="me-2" />
          Manage Tabs
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <div className="text-center text-muted py-5">Loading tabs…</div>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row>
                <Col md={5}>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">
                      <Badge bg="secondary" className="me-2">
                        {getFilteredAvailable().length}
                      </Badge>
                      Hidden tabs
                    </h6>
                  </div>
                  <div className="mb-3">
                    <div className="field-search-container">
                      <FaSearch className="search-icon" />
                      <Form.Control
                        type="text"
                        placeholder="Search hidden tabs…"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="field-search-input"
                        style={{
                          paddingLeft: '36px',
                          paddingRight: searchTerm ? '36px' : '12px',
                        }}
                      />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => setSearchTerm('')}
                          className="clear-search-btn"
                          title="Clear search"
                        >
                          <FaTimes size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div
                    className="manage-tabs-panel border rounded p-3"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    {getFilteredAvailable().length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <p className="mb-0">No hidden tabs</p>
                        <small>Remove tabs from the right panel to hide them</small>
                      </div>
                    ) : (
                      getFilteredAvailable().map((tab) => (
                        <div
                          key={tab.id}
                          className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border"
                          style={{ cursor: 'pointer' }}
                          onClick={() => moveToSelected(tab)}
                        >
                          <div>
                            <div className="fw-bold">{tab.label}</div>
                            {tab.group_key && (
                              <small className="text-muted d-block">
                                Group: {tab.group_label || tab.group_key}
                              </small>
                            )}
                            {tab.name && tab.name !== tab.label && (
                              <small className="text-muted">{tab.name}</small>
                            )}
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              moveToSelected(tab);
                            }}
                            title="Show in navigation"
                          >
                            <FaArrowRight />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </Col>

                <Col md={7}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">
                      <Badge bg="primary" className="me-2">
                        {visibleTabCount}
                      </Badge>
                      Visible tabs
                    </h6>
                  </div>
                  <p className="text-muted small mb-3">
                    Drag menu bar rows to reorder top-level tabs and groups. Reorder tabs inside
                    each group below (same as Tab Manager).
                  </p>

                  <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="manage-tabs-bar-section mb-3">
                      <h6 className="text-primary mb-2">Menu bar order</h6>
                      {barSlots.length === 0 ? (
                        <div className="text-center text-muted py-4 border rounded">
                          <p className="mb-0">No visible tabs</p>
                        </div>
                      ) : (
                        <Droppable droppableId="manage-tabs-bar-slots">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className="manage-tabs-panel border rounded p-2"
                              style={{ backgroundColor: '#f8f9fa' }}
                            >
                              {barSlots.map((slot, index) => (
                                <Draggable
                                  key={
                                    slot.type === 'tab'
                                      ? `bar-tab-${slot.tab.id}`
                                      : `bar-group-${slot.key}`
                                  }
                                  draggableId={
                                    slot.type === 'tab'
                                      ? `bar-tab-${slot.tab.id}`
                                      : `bar-group-${slot.key}`
                                  }
                                  index={index}
                                  isDragDisabled={
                                    slot.type === 'tab' && isHomeNavTab(slot.tab)
                                  }
                                >
                                  {(dragProvided, snapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      className={`manage-tabs-bar-slot d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border ${
                                        snapshot.isDragging ? 'manage-tabs-slot-dragging' : ''
                                      }`}
                                    >
                                      <div className="d-flex align-items-center flex-grow-1">
                                        <span
                                          {...dragProvided.dragHandleProps}
                                          className="manage-tabs-drag-handle me-2 text-muted"
                                          title="Drag to reorder menu bar"
                                        >
                                          <FaGripVertical />
                                        </span>
                                        {slot.type === 'tab' ? (
                                          <div>
                                            <div className="fw-bold">{slot.tab.label}</div>
                                            {isHomeNavTab(slot.tab) && (
                                              <Badge bg="light" text="dark" className="ms-0">
                                                Required
                                              </Badge>
                                            )}
                                          </div>
                                        ) : (
                                          <div>
                                            <span className="manage-tabs-group-label fw-bold">
                                              Group: {slot.label}
                                            </span>
                                            <small className="text-muted d-block">
                                              {slot.tabs.length} tab
                                              {slot.tabs.length !== 1 ? 's' : ''} in dropdown
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                      {slot.type === 'tab' && !isHomeNavTab(slot.tab) && (
                                        <Button
                                          variant="outline-danger"
                                          size="sm"
                                          onClick={() => moveToAvailable(slot.tab)}
                                          title="Hide from navigation"
                                        >
                                          <FaTrash />
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      )}
                    </div>

                    {groupBarSlots.length > 0 && (
                      <div className="manage-tabs-group-section">
                        <h6 className="text-muted mb-2">Tabs within groups</h6>
                        {groupBarSlots.map((slot) => (
                          <Card key={`group-section-${slot.key}`} className="mb-3 manage-tabs-group-card">
                            <Card.Header className="py-2 fw-semibold">
                              {slot.label}
                            </Card.Header>
                            <Card.Body className="p-2">
                              <Droppable droppableId={`manage-tabs-group-${slot.key}`}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                  >
                                    {(slot.tabs || []).map((tab, tabIndex) => (
                                      <Draggable
                                        key={`group-tab-${tab.id}`}
                                        draggableId={`group-tab-${tab.id}`}
                                        index={tabIndex}
                                      >
                                        {(dragProvided, snapshot) => (
                                          <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={`d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border ${
                                              snapshot.isDragging ? 'manage-tabs-slot-dragging' : ''
                                            }`}
                                          >
                                            <div className="d-flex align-items-center">
                                              <span
                                                {...dragProvided.dragHandleProps}
                                                className="manage-tabs-drag-handle me-2 text-muted"
                                              >
                                                <FaGripVertical />
                                              </span>
                                              <span className="fw-medium">{tab.label}</span>
                                            </div>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => moveToAvailable(tab)}
                                              title="Hide from navigation"
                                            >
                                              <FaTrash />
                                            </Button>
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </DragDropContext>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading || saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ManageTabsModal;
